import React, {useMemo} from 'react'
import {buildPedigreeDataNode, groupUnitsForRender} from '../functions/pedigreeTree'
import PedigreeBox, {PEDIGREE_BOX_WIDTH} from './PedigreeBox'

// Renderer THỬ NGHIỆM song song của Cây Gia Phả - vẽ bằng TOẠ ĐỘ TÍNH TRƯỚC (đo kích thước từng nhánh
// trước, gán x/y sau - kiểu thuật toán layout cây kinh điển) thay vì suy luận vị trí qua CSS ul/li
// (PEDIGREE_TREE_CSS ở FamilyBooks.js, dùng ::before/::after + border để "đoán" đường nối theo vị trí
// flexbox). Đường nối vẽ bằng SVG <line> (nối đúng 2 toạ độ ĐÃ BIẾT, không suy luận), ô người vẫn là
// <div> HTML thường (tái dùng PedigreeBox NGUYÊN VẸN, giữ đúng style/badge/onClick hiện có) đặt
// position:absolute theo toạ độ đã tính, xếp LỚP TRÊN 1 <svg> chỉ vẽ đường nối (LỚP DƯỚI,
// pointerEvents:none) - KHÔNG dùng SVG <foreignObject> để nhúng luôn ô người vào trong SVG (rủi ro
// tương thích WebView khi đóng gói Cordova/mobile, xem CLAUDE.md "mọi tính năng phải hoạt động khi
// đóng gói Cordova") - tách lớp là cách an toàn hơn, cũng giữ nguyên được toàn bộ hành vi click/hover
// đã có của PedigreeBox.
//
// Vì có toạ độ THẬT (không suy luận qua flexbox), đường nối xuống hàng con giờ xuất phát ĐÚNG từ điểm
// hôn phối (giữa 2 vợ/chồng) rồi mới "gãy góc" xuống đúng cột con - đây CHÍNH LÀ điều đã thử làm bằng
// CSS margin tính tay trước đó và làm VỠ layout khi có >1 con (xem lịch sử MultiMarriageGroup ở
// FamilyBooks.js, phản hồi thực tế "quát nát") - giờ AN TOÀN vì mọi con số đều ĐO THẬT bằng JS, không
// còn suy luận/áng chừng qua CSS nữa.
//
// Riêng nhánh multi-spouse (1 người nhiều vợ/chồng): con của MỖI nhánh vẫn đặt dưới ĐÚNG cột vợ/chồng
// của nhánh đó (KHÔNG dồn về đúng điểm giữa hôn phối như trường hợp 1-vợ/1-chồng ở trên) - dồn về
// đúng giữa cho cả trường hợp này cần 1 thuật toán đo "contour" 2 chiều phức tạp hơn hẳn (dễ chồng lấn
// giữa 2 nhánh vợ/chồng cạnh nhau khi 1 bên đông con hơn hẳn bên kia) - giữ compromise AN TOÀN giống hệt
// bản CSS ổn định hiện tại cho riêng phần này, nâng cấp sau nếu thực sự cần.
const BOX_W = PEDIGREE_BOX_WIDTH
// Chiều cao ước lượng 1 ô - PedigreeBox tự co theo nội dung (badge linh mục/tu sĩ, ⋯ báo cắt nhánh...)
// nên có thể lệch vài px so với thực tế trong các trường hợp hiếm (ô có badge sẽ cao hơn ước lượng
// này 1 chút) - chấp nhận được ở bản thử nghiệm, đường nối vẫn chạm đúng cạnh trên/dưới ô ở đại đa số
// trường hợp (ô không badge, chiếm phần lớn).
const BOX_H = 70
const SPOUSE_GAP = 24
const SIBLING_GAP = 18
const ROW_H = 96
// Đoạn dọc trước khi rẽ ngang - tăng từ 18 lên 22 để đoạn "gãy góc" (dogleg, xem addDescentLines) ở
// nhánh multi-spouse có đủ chỗ thở, tránh 2 khúc rẽ dồn sát nhau nhìn rối ở mức zoom cao (phản hồi
// thực tế "đường nối không rõ ràng").
const STUB = 22
const PADDING = 24
// family-300 (#fcd34d, vàng nhạt) tương phản quá thấp trên nền family-50 (#fffbeb, kem) - phản hồi
// thực tế "đường nối không rõ ràng" khi xem kỹ/zoom. Đổi qua family-600 (#d97706, cam đậm - CÙNG màu
// đang dùng cho nút bấm chính/viền nhấn mạnh trong app) để tương phản đủ rõ trên nền kem.
const LINE_COLOR = 'var(--color-family-600)'
const LINE_WIDTH = 2.5

// Trả về 1 path đơn giản (1 đoạn thẳng) cho đường nối hôn phối - dùng chung format {type:'path', d}
// với addDescentLines() để render đồng nhất 1 kiểu <path> duy nhất (xem SvgPedigreeTree).
const marriageLine = (cx1, cx2, y) => {
    const x1 = Math.min(cx1, cx2) + BOX_W / 2
    const x2 = Math.max(cx1, cx2) - BOX_W / 2
    const yMid = y + BOX_H / 2
    return {type: 'path', d: `M${x1},${yMid} L${x2},${yMid}`}
}

// Đường xuống hàng con - LUÔN xuất phát từ điểm hôn phối (unionCx), gãy góc sang đúng cột con
// (targetCx) nếu 2 điểm này khác nhau (trường hợp multi-spouse, xem giải thích ở đầu file) - tự
// "thẳng luôn" khi unionCx === targetCx (trường hợp 1-vợ/1-chồng hoặc solo, không cần code riêng).
//
// Gộp TẤT CẢ đoạn (thân xuống, gãy góc, thanh ngang nối các con, các nhánh rẽ dọc xuống từng ô con)
// vào CHUNG 1 <path> (nhiều subpath "M..L.." nối tiếp trong cùng 1 chuỗi d) thay vì nhiều <line> rời
// rạc như bản đầu - 1 path duy nhất tô 1 lần, góc rẽ mượt hơn (strokeLinejoin="round" áp dụng nhất
// quán tại mọi điểm gãy), không còn hiện tượng nhiều nét mảnh chồng sát nhau nhìn rối khi zoom cao
// (phản hồi thực tế "đường nối không rõ ràng").
const addDescentLines = (lines, unionCx, targetCx, y, childY, centers) => {
    if (!centers.length) return
    const midY = y + BOX_H + STUB
    const barY = childY - STUB
    let d = Math.abs(unionCx - targetCx) > 0.5
        ? `M${unionCx},${y + BOX_H} L${unionCx},${midY} L${targetCx},${midY} L${targetCx},${barY} `
        : `M${targetCx},${y + BOX_H} L${targetCx},${barY} `
    // Thanh ngang nối các con (chỉ cần khi >1 con) rồi rẽ dọc xuống ĐÚNG đỉnh từng ô con - giống hệt
    // tinh thần đường nối chữ L của bản CSS, chỉ khác là toạ độ đo thật thay vì suy luận.
    if (centers.length > 1) {
        d += `M${Math.min(...centers)},${barY} L${Math.max(...centers)},${barY} `
    }
    centers.forEach((cx) => { d += `M${cx},${barY} L${cx},${childY} ` })
    lines.push({type: 'path', d})
}

// Đo TRƯỚC bề rộng cần thiết của từng nhánh (bottom-up: con đo trước, cha đo sau dựa trên tổng bề
// rộng các con) - mutate thẳng lên node (width, childrenWidth/branchWidths) vì cây này chỉ sống trong
// 1 lượt tính layout (useMemo bên dưới), không phải state cần giữ nguyên tham chiếu.
const measure = (node) => {
    if (!node) return 0
    if (node.kind === 'single') {
        const ownWidth = node.spouse ? (BOX_W * 2 + SPOUSE_GAP) : BOX_W
        let childrenWidth = 0
        node.children.forEach((c, i) => { childrenWidth += measure(c) + (i > 0 ? SIBLING_GAP : 0) })
        node.childrenWidth = childrenWidth
        node.width = Math.max(ownWidth, childrenWidth)
        return node.width
    }
    // multi - mỗi nhánh (1 cuộc hôn nhân) tự đo bề rộng riêng, tối thiểu đủ chỗ cho 1 ô vợ/chồng.
    node.branches.forEach((b) => {
        let childrenWidth = 0
        b.children.forEach((c, i) => { childrenWidth += measure(c) + (i > 0 ? SIBLING_GAP : 0) })
        b.childrenWidth = childrenWidth
        b.width = Math.max(BOX_W, childrenWidth)
    })
    const mid = Math.ceil(node.branches.length / 2)
    node.leftCount = mid
    const leftTotal = node.branches.slice(0, mid).reduce((s, b) => s + b.width + SPOUSE_GAP, 0)
    const rightTotal = node.branches.slice(mid).reduce((s, b) => s + b.width + SPOUSE_GAP, 0)
    node.width = leftTotal + BOX_W + rightTotal
    return node.width
}

// Gán TOẠ ĐỘ THẬT (top-down: đã biết bề rộng cần thiết từ measure(), giờ chỉ cần đặt vào đúng chỗ) -
// đẩy kết quả (box cần vẽ, đường nối cần vẽ) vào 2 mảng dùng chung `boxes`/`lines` qua tham chiếu.
const place = (node, xLeft, y, boxes, lines, bounds) => {
    if (!node) return
    bounds.maxY = Math.max(bounds.maxY, y + BOX_H)
    if (node.kind === 'single') {
        const cx = xLeft + node.width / 2
        node.cx = cx
        const primaryCx = node.spouse ? cx - (SPOUSE_GAP + BOX_W) / 2 : cx
        const spouseCx = node.spouse ? cx + (SPOUSE_GAP + BOX_W) / 2 : null
        boxes.push({key: `${node.key}_p`, person: node.primary, cx: primaryCx, y, truncated: node.truncated, elsewhere: node.duplicate, inLaw: false})
        let unionCx = primaryCx
        if (node.spouse) {
            boxes.push({key: `${node.key}_s`, person: node.spouse, cx: spouseCx, y, truncated: false, elsewhere: node.duplicate, inLaw: true})
            lines.push(marriageLine(primaryCx, spouseCx, y))
            unionCx = cx // trung điểm 2 ô, luôn === cx vì primary/spouse đặt đối xứng quanh cx
        }
        if (node.children.length) {
            const childY = y + ROW_H
            let cursor = cx - node.childrenWidth / 2
            const centers = []
            node.children.forEach((child, i) => {
                if (i > 0) cursor += SIBLING_GAP
                place(child, cursor, childY, boxes, lines, bounds)
                centers.push(child.cx)
                cursor += child.width
            })
            addDescentLines(lines, unionCx, cx, y, childY, centers)
        }
        return
    }
    // multi - chia trái/phải quanh primary (giống MultiMarriageGroup ở bản CSS), lệch thì bên phải
    // nhiều hơn 1 (node.leftCount = ceil/2, tính ở measure()).
    let cursor = xLeft
    const placed = []
    node.branches.slice(0, node.leftCount).forEach((b) => {
        const bx = cursor
        placed.push({b, bx, spouseCx: bx + b.width / 2})
        cursor += b.width + SPOUSE_GAP
    })
    const primaryCx = cursor + BOX_W / 2
    cursor += BOX_W
    node.branches.slice(node.leftCount).forEach((b) => {
        cursor += SPOUSE_GAP
        const bx = cursor
        placed.push({b, bx, spouseCx: bx + b.width / 2})
        cursor += b.width
    })
    node.cx = primaryCx
    boxes.push({key: `${node.key}_p`, person: node.primary, cx: primaryCx, y, truncated: false, inLaw: false})
    placed.forEach(({b, bx, spouseCx}, i) => {
        boxes.push({key: `${node.key}_s${i}`, person: b.spouse, cx: spouseCx, y, truncated: b.truncated, elsewhere: b.duplicate, inLaw: true, spouseOrder: i + 1})
        lines.push(marriageLine(primaryCx, spouseCx, y))
        const unionCx = (primaryCx + spouseCx) / 2
        if (b.children.length) {
            const childY = y + ROW_H
            // Con đặt giữa ĐÚNG khoang đã đo riêng cho nhánh này ([bx, bx+b.width], tương đương canh
            // giữa dưới spouseCx) - xem giải thích compromise ở đầu file.
            let ccursor = bx + (b.width - b.childrenWidth) / 2
            const centers = []
            b.children.forEach((child, j) => {
                if (j > 0) ccursor += SIBLING_GAP
                place(child, ccursor, childY, boxes, lines, bounds)
                centers.push(child.cx)
                ccursor += child.width
            })
            addDescentLines(lines, unionCx, spouseCx, y, childY, centers)
        }
    })
}

// `rootUnits`: mảng unit CHƯA gom nhóm (giống dữ liệu FullPedigreeModal truyền cho
// groupUnitsForRender(rootUnits) ở renderer CSS) - tự gom nhóm + dựng cây dữ liệu bên trong, cùng 1
// nguồn dữ liệu/logic với renderer CSS (childrenByEffectiveParent, childrenByCouple, byKey), chỉ khác
// bước cuối là TÍNH TOẠ ĐỘ + vẽ SVG thay vì trả JSX ul/li lồng nhau.
const SvgPedigreeTree = ({rootUnits, byKey, childrenByEffectiveParent, childrenByCouple, onViewDetail, t}) => {
    const layout = useMemo(() => {
        // Set MỚI mỗi lần tính lại (không useMemo giữ qua lần render khác) - cùng lý do với `visited`
        // ở FullPedigreeModal: nếu giữ nguyên qua các lần render sau, lần thứ 2 trở đi mọi người đều
        // "đã visited" ngay từ đầu, cây biến mất trắng trơn.
        const visited = new Set()
        const ctx = {byKey, childrenByEffectiveParent, childrenByCouple, visited}
        const roots = groupUnitsForRender(rootUnits).map((g) => buildPedigreeDataNode(g, 1, ctx)).filter(Boolean)
        roots.forEach((node) => measure(node))
        const boxes = []
        const lines = []
        const bounds = {maxY: 0}
        let cursor = PADDING
        roots.forEach((node, i) => {
            if (i > 0) cursor += SIBLING_GAP * 2
            place(node, cursor, PADDING, boxes, lines, bounds)
            cursor += node.width
        })
        return {boxes, lines, width: Math.max(cursor + PADDING, 1), height: Math.max(bounds.maxY + PADDING, 1)}
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rootUnits, byKey, childrenByEffectiveParent, childrenByCouple])

    return (
        <div style={{position: 'relative', width: layout.width, height: layout.height}}>
            <svg
                width={layout.width}
                height={layout.height}
                style={{position: 'absolute', top: 0, left: 0, pointerEvents: 'none'}}>
                {layout.lines.map((ln, i) => (
                    <path key={i} d={ln.d} stroke={LINE_COLOR} strokeWidth={LINE_WIDTH} fill="none" strokeLinecap="round" strokeLinejoin="round"/>
                ))}
            </svg>
            {layout.boxes.map((b) => (
                <div key={b.key} style={{position: 'absolute', top: b.y, left: b.cx - BOX_W / 2}}>
                    {/* depth suy ngược từ y (đặt CHÍNH XÁC = PADDING + (depth-1)*ROW_H lúc place(),
                        không lệch) - đủ để tính nhãn vai trò (computePedigreeRoleLabel) mà không cần
                        thread thêm depth qua measure()/place(). */}
                    <PedigreeBox
                        person={b.person}
                        inLaw={b.inLaw}
                        truncated={b.truncated}
                        elsewhere={b.elsewhere}
                        depth={Math.round((b.y - PADDING) / ROW_H) + 1}
                        spouseOrder={b.spouseOrder}
                        onViewDetail={onViewDetail}
                        t={t}
                    />
                </div>
            ))}
        </div>
    )
}

export default SvgPedigreeTree
